import React from 'react';
import { ServiceTypeTemplate } from '../../templates/ServiceTypeTemplate';
import { useAttendanceUnitsWithDates } from '../../../../hooks/useAttendanceUnitsWithDates';
import { CoverageProps } from '../../../../models/Product';
import { useScheduleContext } from '../../../../contexts/ScheduleContext';
import { Modals, useModalsContext } from '../../../../contexts/ModalsContext';
import { Unit } from '../../../../components/ServiceItem';
import { LoadingOverlay } from '../../../../components';
import { ModalScheduleNotNecessary } from '../../../../components/ModalScheduleNotNecessary';
import { ScheduleItemPropsBuilder } from '../../../../models/Schedule';
import { Page, useExecutiveCheckupRouter } from '../../router/context';
import { useServiceContext } from '../../../../contexts/ServiceContext';
import { addDays, differenceInDays } from 'date-fns';
import { envConfig } from '../../../../utils/enviroment';
import { getUnitIdAndLocalId } from '../../../../utils/location';
import { useRemoteConfig } from '../../../../hooks/useRemoteConfig';

type ServiceTypeProps = {
  isFirstAppointment?: boolean;
  firstAppointmentDate?: string;
};

export const ServiceType = ({ firstAppointmentDate, isFirstAppointment }: ServiceTypeProps) => {
  const { selectedItem: scheduleSelected, changeSchedule } = useScheduleContext();
  const { selectedService: service } = useServiceContext();
  const { navigate, goBack } = useExecutiveCheckupRouter();
  const { openModal } = useModalsContext();

  const { getRemoteConfigValue } = useRemoteConfig();

  const [unitDates, setUnitDates] = React.useState<Record<string, string[]>>({});

  const [selectedCoverage, setSelectedCoverage] = React.useState<CoverageProps>({} as CoverageProps);
  const [selectedUnit, setSelectedUnit] = React.useState<Unit>();

  const [unavailableScheduleUnits, setUnavailableScheduleUnits] = React.useState<Unit[]>([]);
  const [availableScheduleUnits, setAvailableScheduleUnits] = React.useState<Unit[]>([]);
  const [unitOnDemand, setUnitOnDemand] = React.useState<Unit>();

  const productSchedule = React.useMemo(() => {
    return scheduleSelected && service.products.find(product => product.id === scheduleSelected.productId);
  }, [scheduleSelected]);

  const handleSelectedCoverage = async (coverage: CoverageProps) => {
    setSelectedUnit(undefined);

    if (coverage.appointment_status === 'WHATSAPP_DIRECTION') openModal(Modals.SERVICE_UNAVAILABLE);

    if (coverage.appointment_status !== 'WHATSAPP_DIRECTION') setSelectedCoverage(coverage);
  };

  React.useEffect(() => {
    if (openToScheduleUnits.length && unitDatesAvailable.length) {
      const openUnits = [] as Unit[];
      const closeUnits = [] as Unit[];

      openToScheduleUnits.forEach(unit => {
        if (unitDates[getUnitIdAndLocalId(unit)].length) openUnits.push(unit);
        else closeUnits.push(unit);
      });

      setAvailableScheduleUnits(openUnits);
      setUnavailableScheduleUnits(closeUnits);
    }
  }, [unitDates]);

  const saveCoverageAndUnit = (unit: Unit) => {
    const scheduleFactory = ScheduleItemPropsBuilder();

    scheduleFactory.setChannel(selectedCoverage?.channel || '');
    scheduleFactory.setScheduleType({ careType: selectedCoverage.care_type });

    if (unit) scheduleFactory.setUnit(unit, unit?.local_id || unit?.locations?.[0]?.name || '');

    setSelectedUnit(unit);

    changeSchedule({ ...scheduleSelected, ...scheduleFactory.build() });
  };

  const validateReturnsInterval = (): number => {
    if (firstAppointmentDate) {
      const firstAppointment = new Date(firstAppointmentDate);
      return differenceInDays(new Date(), firstAppointment);
    }
    return 0;
  };

  const dateOfFirstAppointment = () => {
    const differenceDays = validateReturnsInterval();

    const INTERVAL_RETURN_DAYS = Number(
      getRemoteConfigValue('checkup_executive_return_days') ?? (envConfig.REACT_APP_DAYS_TO_ALLOW_RETURNS || 0)
    );

    if (differenceDays < INTERVAL_RETURN_DAYS) return addDays(new Date(), INTERVAL_RETURN_DAYS - differenceDays + 1);
    return addDays(new Date(), 1);
  };

  const { loading, openToScheduleUnits, closedToScheduleUnits, unitDatesAvailable } = useAttendanceUnitsWithDates(
    selectedCoverage,
    scheduleSelected,
    undefined,
    () => 0,
    () => 0,
    [],
    dateOfFirstAppointment()
  );

  React.useEffect(() => {
    let convertedUnitDates = {} as Record<string, string[]>;
    unitDatesAvailable.forEach(item => {
      convertedUnitDates = { ...convertedUnitDates, [item.unitSghId]: item.dates };
    });
    setUnitDates(convertedUnitDates);
  }, [unitDatesAvailable]);

  const getFirstAvailableDate = () => {
    const firstAvailableDate = unitDates[selectedUnit?.id_sgh ?? '']?.[0];
    if (firstAvailableDate) return new Date(firstAvailableDate);
    return dateOfFirstAppointment();
  };

  const handleSubmitForm = () =>
    navigate(Page.PRODUCT_SLOTS, { isFirstAppointment, dateOfFirstAppointment: getFirstAvailableDate() });

  React.useEffect(() => {
    const firstUnit = selectedCoverage?.units?.[0];
    if (selectedCoverage?.care_type === 'TELEMEDICINE' && firstUnit) {
      saveCoverageAndUnit(firstUnit);
    }
  }, [selectedCoverage?.care_type]);

  React.useEffect(() => {
    if (
      selectedUnit &&
      selectedCoverage?.care_type === 'TELEMEDICINE' &&
      unitDates[getUnitIdAndLocalId(selectedUnit)]?.length
    )
      setAvailableScheduleUnits([]);

    if (selectedUnit && !unitDates[getUnitIdAndLocalId(selectedUnit)]?.length) setSelectedUnit(undefined);
  }, [unitDates]);

  if (!productSchedule) return <></>;

  return (
    <>
      <ServiceTypeTemplate
        headTitle="Escolha as opções de atendimento"
        scheduleProduct={productSchedule}
        coverageSelected={selectedCoverage}
        handleSelectCoverage={handleSelectedCoverage}
        selectedUnit={selectedUnit}
        unitsOpenToSchedule={availableScheduleUnits}
        unavailableUnits={unavailableScheduleUnits}
        unitsOnDemand={closedToScheduleUnits}
        unitDates={unitDates}
        handleSelectedUnit={saveCoverageAndUnit}
        handleUnitOnDemand={unit => setUnitOnDemand(unit)}
        disableSubmitButton={!selectedUnit}
        handleContinue={handleSubmitForm}
        goBack={goBack}
      />

      <LoadingOverlay loading={loading} />

      <ModalScheduleNotNecessary
        visible={!!unitOnDemand}
        unit={unitOnDemand}
        onClickClose={() => setUnitOnDemand(undefined)}
      />
    </>
  );
};
